import { useEffect } from "react";
import { COMPANIES_CONSTANTS } from "../_MODULES/_API";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { PALETTE_HEX } from "../../../_Catalogs.Views";
import { FormGrid, FieldInput, FieldTextarea, FieldColor, FieldSwitch, useForm } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { isDuplicateRecord } from "../../../../_UI/recordUniqueness";

const EMPTY_VALUES = {
    name: "", description: "", website: "", contactEmail: "",
    contactPhone: "", color: "#e83a52", notes: "", isActive: true, isFavorite: false,
};

export const CompanyFormModal = ({ isOpen, company = null, companies = [], onClose, onSaved }) => {

    const IS_EDIT = !!company?.id;

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 400,
    });

    const FORM = useForm({
        initialValues: EMPTY_VALUES,

        schema: {
            name: { required: true, minLength: 2 },
            contactEmail: { email: true },
        },

        onSubmit: async (values) => {

            if (isDuplicateRecord({ rows: companies, value: values.name, currentId: company?.id })) {
                FORM.setError("name", "Ya existe una compañía con ese nombre", { focus: true });
                return;
            }

            const PAYLOAD = {
                name: values.name.trim(),
                description: values.description || undefined,
                website: values.website || undefined,
                contactEmail: values.contactEmail || undefined,
                contactPhone: values.contactPhone || undefined,
                color: values.color || undefined,
                notes: values.notes || undefined,
                isActive: values.isActive,
                isFavorite: !!values.isFavorite,
            };

            const RESPONSE = IS_EDIT
                ? await actionMutate({ method: "PATCH", endpoint: COMPANIES_CONSTANTS.UPDATE, successMessage: "Compañía actualizada", id: company.id, data: PAYLOAD })
                : await actionMutate({ method: "POST", endpoint: COMPANIES_CONSTANTS.CREATE, successMessage: "Compañía creada", data: PAYLOAD });

            if (!RESPONSE.success) { FORM.applyApiError(RESPONSE); return; }

            onSaved?.(RESPONSE.data, IS_EDIT);
            onClose?.();
        },
    });

    useEffect(() => {

        if (!isOpen) return;

        FORM.setValues(IS_EDIT ? {
            name: company.name ?? "",
            description: company.description ?? "",
            website: company.website ?? "",
            contactEmail: company.contactEmail ?? "",
            contactPhone: company.contactPhone ?? "",
            color: company.color ?? "#e83a52",
            notes: company.notes ?? "",
            isActive: company.isActive ?? true,
            isFavorite: !!company.isFavorite,
        } : EMPTY_VALUES);

    }, [isOpen, company]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <AppModal
            isOpen={isOpen}
            onClose={onClose}
                isDirty={FORM.isDirty}
            size="lg"
            title={IS_EDIT ? "Editar compañía" : "Nueva compañía"}
            description="Los proyectos se cargan después, dentro de la compañía."
            footer={
                <>
                    <AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton>
                    <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading}>
                        {IS_EDIT ? "Guardar cambios" : "Crear compañía"}
                    </AppButton>
                </>
            } >

            <div className="LStudio__app_priority_toggle">
                <FieldSwitch id="company_isFavorite" label="Destacado" text="Mostrar esta compañía primero"
                    isChecked={!!FORM.values.isFavorite}
                    onChange={(event) => FORM.setValue("isFavorite", event.target.checked)} />
            </div>

            <FormGrid columns={3}>

                <FieldSwitch id="isActive" label="Estado" text="Compañía activa"
                    isChecked={FORM.values.isActive}
                    onChange={(event) => FORM.setValue("isActive", event.target.checked)} />

                <FieldInput {...FORM.field("name")} label="Nombre" placeholder="Ej: Empresa Demo" span={3} />

                <FieldTextarea {...FORM.field("description")} label="Descripción" rows={3}
                    placeholder="Ej: Empresa de servicios profesionales" span={3} />

                <FieldInput {...FORM.field("website")} label="Sitio web" placeholder="Ej: https://app.example.com" />

                <FieldInput {...FORM.field("contactEmail")} type="email" label="Correo de contacto"
                    placeholder="Ej: contacto@notebook.local" />

                <FieldInput {...FORM.field("contactPhone")} label="Teléfono" placeholder="Ej: +54 341 555 1234" />

                <FieldColor {...FORM.field("color")} label="Color" swatches={PALETTE_HEX} />

                <FieldTextarea {...FORM.field("notes")} label="Notas internas" rows={3} span={3} placeholder="Ej: Renovar el dominio en marzo" />

            </FormGrid>

        </AppModal>
    );
};
